<template>
  <div class="product-page-price-wrapper">
    <div class="prices">
      <div 
        v-show="discountPrice"
        class="current-price discount-price"
      >
        {{ discountPrice }}
      </div>

      <div 
        v-show="regularPrice" 
        :class="{
          'has-discount': discountPrice,
          'current-price': !discountPrice
        }"
      >
        {{ regularPrice }}
      </div>

      <div 
        v-show="calculatedDiscountPercentage"
        class="discount-percentage"
      >
        {{ calculatedDiscountPercentage }}
      </div>
    </div>

    <div v-show="discountPrice">
      <span class="lowest-price-thirty-days">
        {{ $translate('LOWEST_PRICE_DURING_PREVIOUS_30_DAYS') }} {{ lowestPrice30Days }}
      </span>
    </div>
  </div>
</template>

<script>
import { onMounted, onUnmounted, ref, computed, inject } from 'vue';
import InfoIcon from '@/assets/icons/information-blue-rounded.svg?component';

export default {
  props: {
    price: {
      type: String,
      required: true,
    },
    priceRaw: {
      type: Number,
      required: true,
    },
    priceSpecialRaw: {
      type: [Number, null],
      required: false,
    },
    priceSpecial: {
      type: [String, null],
      required: false,
    },
    priceDiscountPercentage: {
      type: [Number, null],
      required: false,
    },
    priceLogPrice: {
      type: [String, null],
      required: false,
    },
    pricePerUnit: {
      type: [String, null],
      required: false,
    },
    tax: {
      type: Number,
      required: true,
    },
    finalPriceExVatRaw: {
      type: Number,
      required: true,
    },
    showVat: {
      type: Boolean,
      required: true
    },
    updateOnChange: {
      type: Boolean,
      required: false
    }
  },
  components: {
    InfoIcon
  },
  setup(props) {
    const { $translate } = inject('i18n');
    const price = ref(props.price);
    const priceRaw = ref(props.priceRaw)
    const priceSpecial = ref(props.priceSpecial);
    const priceSpecialRaw = ref(props.priceSpecialRaw);
    const pricePerUnit = ref(props.pricePerUnit);
    const productConfiguratorPrice = ref(null);
    const discountPercentage = ref(props.priceDiscountPercentage);
    let vendreConfig = {};

    /** This is to prevent the wrong price on product with attributes 
     * since attributes adds cost to product */
    const intialPriceLoaded = ref(false);

    const productTax = computed(() => {
      return (props.tax / 100) + 1;
    });

    const vatText = computed(() => {
      if (props.showVat) {
        return $translate('EXCL_VAT') + ' ' + window.VendreCurrencyFormatter(props.finalPriceExVatRaw);
      }
      return $translate('INCL_VAT') + ' ' + tooltipExVatPrice.value;
    });

    const tooltipExVatPrice = computed(() => {
      const productPrice = discountPriceRaw.value ? priceSpecialRaw.value : priceRaw.value;
      const productPriceExVat = productPrice * productTax.value;

      return window.VendreCurrencyFormatter(productPriceExVat);
    });
    
    const discountPrice = computed(() => {
      if (priceSpecialRaw.value) {
        return priceSpecial.value;
      }
    });

    const discountPriceRaw = computed(() => {
      if (priceSpecialRaw.value) {
        return priceSpecialRaw.value;
      }
    });

    const regularPrice = computed(() => {
      if (!intialPriceLoaded.value) {
        return '';
      }

      if (productConfiguratorPrice.value !== null) {
        return productConfiguratorPrice.value;
      }

      if (pricePerUnit.value) {
        return pricePerUnit.value;
      } else {
        return price.value;
      }
    });

    const calculatedDiscountPercentage = computed(() => {
      let discount = null;

      if (discountPercentage.value) {
        discount = discountPercentage.value;
      }
      
      return discount ? `(-${discount}%)` : null;
    });

    // Lowest price in 30 days
    const lowestPrice30Days = computed(() => {
      if (props.priceLogPrice) {
        return props.priceLogPrice;
      }

      return price.value;
    });

    const handleUpdatedProductConfiguratorPrice = (event) => {
      productConfiguratorPrice.value = event.detail.price;
    };

    async function getPrice() {
      let formElements = document.querySelector('form[name=add_product]');
      let formData = new FormData(formElements);
      let serializedFormData = new URLSearchParams(formData).toString();
      if (
          formData.get('quantity') === undefined || formData.get('quantity') === null ||
          formData.get('products_id') === undefined || formData.get('products_id') === null
      ) {
        return;
      }

      fetch(`/ajax.php?mode=getProductPrice&hide_ajax_loader=true&${serializedFormData}`, {
        headers: {
          'Accept': 'application/json',
        },
        method: 'POST'
      })
        .then((response) => response.json())
        .then((data) => {
          let price = null;

          if (vendreConfig.product.show_total_final_price) {
            if (data.total_final_price_raw) {
              price = data.total_final_price_raw;
            }
          } else {
            if (data.final_price_per_unit) {
              price = data.final_price_per_unit;
            } else if (data.final_price_raw) {
              price = data.final_price_raw;
            }
          }

          if (discountPrice.value) {
            priceSpecialRaw.value = price;
            priceSpecial.value = window.VendreCurrencyFormatter(price);
            
            const priceDiff = priceRaw.value - priceSpecialRaw.value;
            discountPercentage.value = Math.round((priceDiff / priceRaw.value) * 100);
          } else {
            priceRaw.value = price;
            if (pricePerUnit.value) {
              pricePerUnit.value = window.VendreCurrencyFormatter(price);
            } else {
              price.value = window.VendreCurrencyFormatter(price);
            }
          }
        })
        .catch(error => {
          //
        });
    };

    onMounted(async() => {
      if (props.updateOnChange) {
        await getPrice();
        let productForm = document.querySelector('form[name=add_product]');

        if (productForm) {
          productForm.addEventListener('input', function (e) {
            getPrice();
          });
        }
      }

      document.addEventListener(
        'vendreProductPageProductConfiguratorPriceUpdated',
        handleUpdatedProductConfiguratorPrice
      );

      intialPriceLoaded.value = true;

      vendreConfig = window.vendre_config;
    });

    onUnmounted(() => {
      document.removeEventListener(
        'vendreProductPageProductConfiguratorPriceUpdated',
        handleUpdatedProductConfiguratorPrice
      );
    });

    onUnmounted(() => {
      document.removeEventListener(
        'vendreProductPageProductConfiguratorPriceUpdated',
        handleUpdatedProductConfiguratorPrice
      );
    });

    return {
      InfoIcon,
      vatText,
      regularPrice,
      lowestPrice30Days,
      calculatedDiscountPercentage,
      discountPrice,
    }
  }
}
</script>